import {
  assertUnreachable
} from "./chunk-35HYSA7K.js";
import {
  getLogger
} from "./chunk-TGNFA6PQ.js";

// src/common/network.ts
var MAX_CONN_PARAMS_SIZE = 4096;

// src/client/errors.ts
var ActorClientError = class extends Error {
};
var InternalError = class extends ActorClientError {
};
var ManagerError = class extends ActorClientError {
  constructor(error, opts) {
    super(`Manager error: ${error}`, opts);
  }
};
var ConnParamsTooLong = class extends ActorClientError {
  constructor() {
    super(
      `Connection parameters must be less than ${MAX_CONN_PARAMS_SIZE} bytes`
    );
  }
};
var MalformedResponseMessage = class extends ActorClientError {
  constructor(cause) {
    super(`Malformed response message: ${cause}`, { cause });
  }
};
var NoSupportedTransport = class extends ActorClientError {
  constructor() {
    super("No supported transport available between client and server");
  }
};
var ActionError = class extends ActorClientError {
  constructor(code, message, metadata) {
    super(message);
    this.code = code;
    this.metadata = metadata;
  }
};

// src/client/handle.ts
import * as cbor from "cbor-x";

// src/client/log.ts
var LOGGER_NAME = "actor-client";
function logger() {
  return getLogger(LOGGER_NAME);
}

// src/client/utils.ts
function messageLength(message) {
  if (message instanceof Blob) {
    return message.size;
  }
  if (message instanceof ArrayBuffer) {
    return message.byteLength;
  }
  if (message instanceof Uint8Array) {
    return message.byteLength;
  }
  if (typeof message === "string") {
    return message.length;
  }
  assertUnreachable(message);
}

// src/client/handle.ts
import pRetry from "p-retry";
var CONNECT_SYMBOL = Symbol("connect");
var ActorHandleRaw = class {
  // TODO: ws message queue
  /**
   * Do not call this directly.
   *
   * Creates an instance of ActorHandleRaw.
   *
   * @param {string} endpoint - The endpoint to connect to.
   *
   * @protected
   */
  constructor(client, endpoint, params, encodingKind, supportedTransports, serverTransports, dynamicImports) {
    this.client = client;
    this.endpoint = endpoint;
    this.params = params;
    this.encodingKind = encodingKind;
    this.supportedTransports = supportedTransports;
    this.serverTransports = serverTransports;
    this.dynamicImports = dynamicImports;
    this.#keepNodeAliveInterval = setInterval(() => 6e4);
  }
  #disposed = false;
  /* Will be aborted on dispose. */
  #abortController = new AbortController();
  /** If attempting to connect. Helpful for knowing if in a retry loop when reconnecting. */
  #connecting = false;
  // These will only be set on SSE driver
  #connectionId;
  #connectionToken;
  #transport;
  #messageQueue = [];
  #rpcInFlight = /* @__PURE__ */ new Map();
  // biome-ignore lint/suspicious/noExplicitAny: Unknown subscription type
  #eventSubscriptions = /* @__PURE__ */ new Map();
  #rpcIdCounter = 0;
  /**
   * Interval that keeps the NodeJS process alive if this is the only thing running.
   *
   * See ttps://github.com/nodejs/node/issues/22088
   */
  #keepNodeAliveInterval;
  /** Promise used to indicate the socket has connected successfully. This will be rejected if the connection fails. */
  #onOpenPromise;
  /**
   * Call a raw RPC handle. See {@link ActorHandle} for type-safe RPC calls.
   *
   * @see {@link ActorHandle}
   * @template Args - The type of arguments to pass to the RPC function.
   * @template Response - The type of the response returned by the RPC function.
   * @param {string} name - The name of the RPC function to call.
   * @param {...Args} args - The arguments to pass to the RPC function.
   * @returns {Promise<Response>} - A promise that resolves to the response of the RPC function.
   */
  async action(name, ...args) {
    logger().debug("action", { name, args });
    const rpcId = this.#rpcIdCounter;
    this.#rpcIdCounter += 1;
    const { promise, resolve, reject } = Promise.withResolvers();
    this.#rpcInFlight.set(rpcId, { name, resolve, reject });
    this.#sendMessage({
      b: {
        rr: {
          i: rpcId,
          n: name,
          a: args
        }
      }
    });
    const { i: responseId, o: output } = await promise;
    if (responseId !== rpcId)
      throw new Error(
        `Request ID ${rpcId} does not match response ID ${responseId}`
      );
    return output;
  }
  //async #rpcHttp<Args extends Array<unknown> = unknown[], Response = unknown>(name: string, ...args: Args): Promise<Response> {
  //	const origin = `${resolved.isTls ? "https": "http"}://${resolved.publicHostname}:${resolved.publicPort}`;
  //	const url = `${origin}/rpc/${encodeURIComponent(name)}`;
  //	const res = await fetch(url, {
  //		method: "POST",
  //		// TODO: Import type from protocol
  //		body: JSON.stringify({
  //			args,
  //		})
  //	});
  //	if (!res.ok) {
  //		throw new Error(`RPC error (${res.statusText}):\n${await res.text()}`);
  //	}
  //	// TODO: Import type from protocol
  //	const resJson: httpRpc.ResponseOk<Response> = await res.json();
  //	return resJson.output;
  //}
  /**
  	 * Do not call this directly.
  enc
  	 * Establishes a connection to the server using the specified endpoint & encoding & driver.
  	 *
  	 * @protected
  	 */
  [CONNECT_SYMBOL]() {
    this.#connectWithRetry();
  }
  async #connectWithRetry() {
    this.#connecting = true;
    await pRetry(this.#connectAndWait.bind(this), {
      forever: true,
      minTimeout: 250,
      maxTimeout: 3e4,
      onFailedAttempt: (error) => {
        logger().warn("failed to reconnect", {
          attempt: error.attemptNumber,
          error: `${error}`
        });
      },
      // Cancel retry if aborted
      signal: this.#abortController.signal
    });
    this.#connecting = false;
  }
  async #connectAndWait() {
    try {
      if (this.#onOpenPromise)
        throw new Error("#onOpenPromise already defined");
      this.#onOpenPromise = Promise.withResolvers();
      const transport = this.#pickTransport();
      if (transport === "websocket") {
        this.#connectWebSocket();
      } else if (transport === "sse") {
        this.#connectSse();
      } else {
        assertUnreachable(transport);
      }
      await this.#onOpenPromise.promise;
    } finally {
      this.#onOpenPromise = void 0;
    }
  }
  #pickTransport() {
    const transport = this.serverTransports.find(
      (t) => this.supportedTransports.includes(t)
    );
    if (!transport) {
      throw new NoSupportedTransport();
    }
    return transport;
  }
  #connectWebSocket() {
    const { WebSocket: WebSocket2 } = this.dynamicImports;
    const url = this.#buildConnUrl("websocket");
    logger().debug("connecting to websocket", { url });
    const ws = new WebSocket2(url);
    if (this.encodingKind === "cbor") {
      ws.binaryType = "arraybuffer";
    } else if (this.encodingKind === "json") {
      try {
        ws.binaryType = "blob";
      } catch (error) {
      }
    } else {
      assertUnreachable(this.encodingKind);
    }
    this.#transport = { websocket: ws };
    ws.onopen = () => {
      logger().debug("websocket open");
    };
    ws.onmessage = async (ev) => {
      this.#handleOnMessage(ev);
    };
    ws.onclose = (ev) => {
      this.#handleOnClose(ev);
    };
    ws.onerror = (ev) => {
      this.#handleOnError(ev);
    };
  }
  #connectSse() {
    const { EventSource: EventSource2 } = this.dynamicImports;
    const url = this.#buildConnUrl("sse");
    logger().debug("connecting to sse", { url });
    const eventSource = new EventSource2(url);
    this.#transport = { sse: eventSource };
    eventSource.onopen = () => {
      logger().debug("eventsource open");
    };
    eventSource.onmessage = (ev) => {
      this.#handleOnMessage(ev);
    };
    eventSource.onerror = (ev) => {
      if (eventSource.readyState === EventSource2.CLOSED) {
        this.#handleOnClose(ev);
      } else {
        this.#handleOnError(ev);
      }
    };
  }
  /** Called by the onopen event from drivers. */
  #handleOnOpen() {
    logger().debug("socket open", {
      messageQueueLength: this.#messageQueue.length
    });
    if (this.#onOpenPromise) {
      this.#onOpenPromise.resolve(void 0);
    } else {
      logger().warn("#onOpenPromise is undefined");
    }
    for (const eventName of this.#eventSubscriptions.keys()) {
      this.#sendSubscription(eventName, true);
    }
    const queue = this.#messageQueue;
    this.#messageQueue = [];
    for (const msg of queue) {
      this.#sendMessage(msg);
    }
  }
  /** Called by the onmessage event from drivers. */
  async #handleOnMessage(event) {
    const response = await this.#parse(event.data);
    if ("i" in response.b) {
      this.#connectionId = response.b.i.ci;
      this.#connectionToken = response.b.i.ct;
      this.#handleOnOpen();
    } else if ("ro" in response.b) {
      const { i: rpcId } = response.b.ro;
      const inFlight = this.#takeRpcInFlight(rpcId);
      inFlight.resolve(response.b.ro);
    } else if ("re" in response.b) {
      const { i: rpcId, c: code, m: message, md: metadata } = response.b.re;
      const inFlight = this.#takeRpcInFlight(rpcId);
      logger().warn("actor error", {
        actionId: rpcId,
        actionName: inFlight == null ? void 0 : inFlight.name,
        code,
        message,
        metadata
      });
      inFlight.reject(new ActionError(code, message, metadata));
    } else if ("ev" in response.b) {
      this.#dispatchEvent(response.b.ev);
    } else if ("er" in response.b) {
      const { c: code, m: message, md: metadata } = response.b.er;
      logger().warn("actor error", {
        code,
        message,
        metadata
      });
    } else {
      assertUnreachable(response.b);
    }
  }
  /** Called by the onclose event from drivers. */
  #handleOnClose(event) {
    if (this.#onOpenPromise) {
      this.#onOpenPromise.reject(new Error("Closed"));
    }
    const closeEvent = event;
    logger().debug("socket closed", {
      code: closeEvent.code,
      reason: closeEvent.reason,
      wasClean: closeEvent.wasClean
    });
    this.#transport = void 0;
    if (!this.#disposed && !this.#connecting) {
      this.#connectWithRetry();
    }
  }
  /** Called by the onerror event from drivers. */
  #handleOnError(event) {
    if (this.#disposed) return;
    logger().warn("socket error", { event });
  }
  #buildConnUrl(transport) {
    let url = `${this.endpoint}/connect/${transport}?encoding=${this.encodingKind}`;
    if (this.params !== void 0) {
      const paramsStr = JSON.stringify(this.params);
      if (paramsStr.length > MAX_CONN_PARAMS_SIZE) {
        throw new ConnParamsTooLong();
      }
      url += `&params=${encodeURIComponent(paramsStr)}`;
    }
    if (transport === "websocket") {
      url = url.replace(/^http:/, "ws:").replace(/^https:/, "wss:");
    }
    return url;
  }
  #takeRpcInFlight(id) {
    const inFlight = this.#rpcInFlight.get(id);
    if (!inFlight) {
      throw new InternalError(`No in flight response for ${id}`);
    }
    this.#rpcInFlight.delete(id);
    return inFlight;
  }
  #dispatchEvent(event) {
    const { n: name, a: args } = event;
    const listeners = this.#eventSubscriptions.get(name);
    if (!listeners) return;
    for (const listener of [...listeners]) {
      listener.callback(...args);
      if (listener.once) {
        listeners.delete(listener);
      }
    }
    if (listeners.size === 0) {
      this.#eventSubscriptions.delete(name);
    }
  }
  #addEventSubscription(eventName, callback, once) {
    const listener = {
      callback,
      once
    };
    let subscriptionSet = this.#eventSubscriptions.get(eventName);
    if (subscriptionSet === void 0) {
      subscriptionSet = /* @__PURE__ */ new Set();
      this.#eventSubscriptions.set(eventName, subscriptionSet);
      this.#sendSubscription(eventName, true);
    }
    subscriptionSet.add(listener);
    return () => {
      const listeners = this.#eventSubscriptions.get(eventName);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) {
          this.#eventSubscriptions.delete(eventName);
          this.#sendSubscription(eventName, false);
        }
      }
    };
  }
  /**
   * Subscribes to an event that will happen repeatedly.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.gg/docs/events|Events Documentation}
   */
  on(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, false);
  }
  /**
   * Subscribes to an event that will be triggered only once.
   *
   * @template Args - The type of arguments the event callback will receive.
   * @param {string} eventName - The name of the event to subscribe to.
   * @param {(...args: Args) => void} callback - The callback function to execute when the event is triggered.
   * @returns {EventUnsubscribe} - A function to unsubscribe from the event.
   * @see {@link https://rivet.gg/docs/events|Events Documentation}
   */
  once(eventName, callback) {
    return this.#addEventSubscription(eventName, callback, true);
  }
  #sendMessage(message, opts) {
    let queueMessage = false;
    if (!this.#transport) {
      queueMessage = true;
    } else if ("websocket" in this.#transport) {
      const { WebSocket: WebSocket2 } = this.dynamicImports;
      if (this.#transport.websocket.readyState === WebSocket2.OPEN) {
        try {
          const messageSerialized = this.#serialize(message);
          this.#transport.websocket.send(messageSerialized);
          logger().debug("sent websocket message", {
            message,
            len: messageLength(messageSerialized)
          });
        } catch (error) {
          logger().warn("failed to send message, added to queue", {
            error
          });
          queueMessage = true;
        }
      } else {
        queueMessage = true;
      }
    } else if ("sse" in this.#transport) {
      const { EventSource: EventSource2 } = this.dynamicImports;
      if (this.#transport.sse.readyState === EventSource2.OPEN) {
        this.#sendHttpMessage(message, opts);
      } else {
        queueMessage = true;
      }
    } else {
      assertUnreachable(this.#transport);
    }
    if (!(opts == null ? void 0 : opts.ephemeral) && queueMessage) {
      this.#messageQueue.push(message);
      logger().debug("queued connection message");
    }
  }
  async #sendHttpMessage(message, opts) {
    try {
      if (!this.#connectionId || !this.#connectionToken)
        throw new InternalError("Missing connection ID or token.");
      let url = `${this.endpoint}/connections/${this.#connectionId}/message?encoding=${this.encodingKind}&connectionToken=${encodeURIComponent(this.#connectionToken)}`;
      const messageSerialized = this.#serialize(message);
      const res = await fetch(url, {
        method: "POST",
        body: messageSerialized
      });
      if (!res.ok) {
        throw new InternalError(
          `Publish message over HTTP error (${res.statusText}):
${await res.text()}`
        );
      }
      await res.json();
    } catch (error) {
      logger().warn("failed to send message, added to queue", {
        error
      });
      if (!(opts == null ? void 0 : opts.ephemeral)) {
        this.#messageQueue.unshift(message);
      }
    }
  }
  async #parse(data) {
    if (this.encodingKind === "json") {
      if (typeof data !== "string") {
        throw new Error("received non-string for json parse");
      }
      return JSON.parse(data);
    } else if (this.encodingKind === "cbor") {
      if (!this.#transport) {
        throw new Error("Cannot parse message when no transport defined");
      } else if ("sse" in this.#transport) {
        if (typeof data === "string") {
          const binaryString = atob(data);
          data = new Uint8Array(
            [...binaryString].map((char) => char.charCodeAt(0))
          );
        } else {
          throw new InternalError(
            `Expected data to be a string for SSE, got ${data}.`
          );
        }
      } else if ("websocket" in this.#transport) {
      } else {
        assertUnreachable(this.#transport);
      }
      if (data instanceof Blob) {
        return cbor.decode(new Uint8Array(await data.arrayBuffer()));
      } else if (data instanceof ArrayBuffer) {
        return cbor.decode(new Uint8Array(data));
      } else if (data instanceof Uint8Array) {
        return cbor.decode(data);
      } else {
        throw new Error(
          `received non-binary type for cbor parse: ${typeof data}`
        );
      }
    } else {
      assertUnreachable(this.encodingKind);
    }
  }
  #serialize(value) {
    if (this.encodingKind === "json") {
      return JSON.stringify(value);
    } else if (this.encodingKind === "cbor") {
      return cbor.encode(value);
    } else {
      assertUnreachable(this.encodingKind);
    }
  }
  /**
   * Disconnects from the actor.
   *
   * @returns {Promise<void>} A promise that resolves when the socket is gracefully closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger().warn("handle already disconnected");
      return;
    }
    this.#disposed = true;
    logger().debug("disposing actor");
    clearInterval(this.#keepNodeAliveInterval);
    this.#abortController.abort();
    this.client[ACTOR_HANDLES_SYMBOL].delete(this);
    if (!this.#transport) {
    } else if ("websocket" in this.#transport) {
      const { promise, resolve } = Promise.withResolvers();
      this.#transport.websocket.addEventListener(
        "close",
        () => resolve(void 0)
      );
      this.#transport.websocket.close();
      await promise;
    } else if ("sse" in this.#transport) {
      this.#transport.sse.close();
    } else {
      assertUnreachable(this.#transport);
    }
    this.#transport = void 0;
  }
  #sendSubscription(eventName, subscribe) {
    this.#sendMessage(
      {
        b: {
          sr: {
            e: eventName,
            s: subscribe
          }
        }
      },
      { ephemeral: true }
    );
  }
};

// src/common/websocket.ts
async function importWebSocket() {
  let _WebSocket;
  if (typeof WebSocket !== "undefined") {
    _WebSocket = WebSocket;
    logger().debug("using native websocket");
  } else {
    try {
      const ws = await import("ws");
      _WebSocket = ws.default;
      logger().debug("using websocket from npm");
    } catch {
      _WebSocket = class MockWebSocket {
        constructor() {
          throw new Error(
            'WebSocket support requires installing the "ws" peer dependency.'
          );
        }
      };
      logger().debug("using mock websocket");
    }
  }
  return _WebSocket;
}

// src/common/eventsource.ts
async function importEventSource() {
  let _EventSource;
  if (typeof EventSource !== "undefined") {
    _EventSource = EventSource;
    logger().debug("using native eventsource");
  } else {
    try {
      const es = await import("eventsource");
      _EventSource = es.EventSource;
      logger().debug("using eventsource from npm");
    } catch (err) {
      _EventSource = class MockEventSource {
        constructor() {
          throw new Error(
            'EventSource support requires installing the "eventsource" peer dependency.'
          );
        }
      };
      logger().debug("using mock eventsource");
    }
  }
  return _EventSource;
}

// src/client/client.ts
var ACTOR_HANDLES_SYMBOL = Symbol("actorHandles");
var ClientRaw2 = class {
  #disposed = false;
  [ACTOR_HANDLES_SYMBOL] = /* @__PURE__ */ new Set();
  #managerEndpointPromise;
  //#regionPromise: Promise<Region | undefined>;
  #encodingKind;
  #supportedTransports;
  // External imports
  #dynamicImportsPromise;
  /**
   * Creates an instance of Client.
   *
   * @param {string | Promise<string>} managerEndpointPromise - The manager endpoint or a promise resolving to it. See {@link https://rivet.gg/docs/setup|Initial Setup} for instructions on getting the manager endpoint.
   * @param {ClientOptions} [opts] - Options for configuring the client.
   * @see {@link https://rivet.gg/docs/setup|Initial Setup}
   */
  constructor(managerEndpointPromise, opts) {
    if (managerEndpointPromise instanceof Promise) {
      this.#managerEndpointPromise = managerEndpointPromise;
    } else {
      this.#managerEndpointPromise = new Promise(
        (resolve) => resolve(managerEndpointPromise)
      );
    }
    this.#encodingKind = (opts == null ? void 0 : opts.encoding) ?? "cbor";
    this.#supportedTransports = (opts == null ? void 0 : opts.supportedTransports) ?? [
      "websocket",
      "sse"
    ];
    this.#dynamicImportsPromise = (async () => {
      const WebSocket2 = await importWebSocket();
      const EventSource2 = await importEventSource();
      return { WebSocket: WebSocket2, EventSource: EventSource2 };
    })();
  }
  /**
   * Gets an actor by its ID.
   * @template AD The actor class that this handle is connected to.
   * @param {string} actorId - The ID of the actor.
   * @param {GetWithIdOptions} [opts] - Options for getting the actor.
   * @returns {Promise<ActorHandle<AD>>} - A promise resolving to the actor handle.
   */
  async getWithId(actorId, opts) {
    logger().debug("get actor with id ", {
      actorId,
      params: opts == null ? void 0 : opts.params
    });
    const resJson = await this.#sendManagerRequest("POST", "/manager/actors", {
      query: {
        getForId: {
          actorId
        }
      }
    });
    const handle = await this.#createHandle(
      resJson.endpoint,
      opts == null ? void 0 : opts.params,
      resJson.supportedTransports
    );
    return this.#createProxy(handle);
  }
  /**
   * Gets an actor by its tags, creating it if necessary.
   *
   * @example
   * ```
   * const room = await client.get<ChatRoom>({
   *   name: 'chat_room',
   *   // Get or create the actor for the channel `random`
   *   channel: 'random'
   * });
   *
   * // This actor will have the tags: { name: 'chat_room', channel: 'random' }
   * await room.sendMessage('Hello, world!');
   * ```
   *
   * @template AD The actor class that this handle is connected to.
   * @param {ActorTags} tags - The tags to identify the actor.
   * @param {GetOptions} [opts] - Options for getting the actor.
   * @returns {Promise<ActorHandle<AD>>} - A promise resolving to the actor handle.
   * @see {@link https://rivet.gg/docs/manage#client.get}
   */
  async get(name, opts) {
    var _a;
    let tags = (opts == null ? void 0 : opts.tags) ?? {};
    let create = void 0;
    if (!(opts == null ? void 0 : opts.noCreate)) {
      create = {
        name,
        // Fall back to tags defined when querying actor
        tags: ((_a = opts == null ? void 0 : opts.create) == null ? void 0 : _a.tags) ?? tags,
        ...opts == null ? void 0 : opts.create
      };
    }
    logger().debug("get actor", {
      name,
      tags,
      parameters: opts == null ? void 0 : opts.params,
      create
    });
    const resJson = await this.#sendManagerRequest("POST", "/manager/actors", {
      query: {
        getOrCreateForTags: {
          name,
          tags,
          create
        }
      }
    });
    const handle = await this.#createHandle(
      resJson.endpoint,
      opts == null ? void 0 : opts.params,
      resJson.supportedTransports
    );
    return this.#createProxy(handle);
  }
  /**
   * Creates a new actor with the provided tags.
   *
   * @example
   * ```
   * // Create a new document actor
   * const doc = await client.create<MyDocument>({
   *   create: {
   *     tags: {
   *       name: 'my_document',
   *       docId: '123'
   *     }
   *   }
   * });
   *
   * await doc.doSomething();
   * ```
   *
   * @template AD The actor class that this handle is connected to.
   * @param {CreateOptions} opts - Options for creating the actor.
   * @returns {Promise<ActorHandle<AD>>} - A promise resolving to the actor handle.
   * @see {@link https://rivet.gg/docs/manage#client.create}
   */
  async create(name, opts) {
    const create = {
      name,
      ...opts.create
    };
    logger().debug("create actor", {
      name,
      parameters: opts == null ? void 0 : opts.params,
      create
    });
    const resJson = await this.#sendManagerRequest("POST", "/manager/actors", {
      query: {
        create
      }
    });
    const handle = await this.#createHandle(
      resJson.endpoint,
      opts == null ? void 0 : opts.params,
      resJson.supportedTransports
    );
    return this.#createProxy(handle);
  }
  async #createHandle(endpoint, params, serverTransports) {
    const imports = await this.#dynamicImportsPromise;
    const handle = new ActorHandleRaw(
      this,
      endpoint,
      params,
      this.#encodingKind,
      this.#supportedTransports,
      serverTransports,
      imports
    );
    this[ACTOR_HANDLES_SYMBOL].add(handle);
    handle[CONNECT_SYMBOL]();
    return handle;
  }
  #createProxy(handle) {
    const methodCache = /* @__PURE__ */ new Map();
    return new Proxy(handle, {
      get(target, prop, receiver) {
        if (typeof prop === "symbol") {
          return Reflect.get(target, prop, receiver);
        }
        if (prop === "then" || prop === "catch" || prop === "finally" || prop === "constructor" || prop in target) {
          const value = Reflect.get(target, prop, receiver);
          if (typeof value === "function") {
            return value.bind(target);
          }
          return value;
        }
        if (typeof prop === "string") {
          let method = methodCache.get(prop);
          if (!method) {
            method = (...args) => target.action(prop, ...args);
            methodCache.set(prop, method);
          }
          return method;
        }
      },
      // Support for 'in' operator
      has(target, prop) {
        if (typeof prop === "string") {
          return true;
        }
        return Reflect.has(target, prop);
      },
      // Support instanceof checks
      getPrototypeOf(target) {
        return Reflect.getPrototypeOf(target);
      },
      // Prevent property enumeration of non-existent RPC methods
      ownKeys(target) {
        return Reflect.ownKeys(target);
      },
      // Support proper property descriptors
      getOwnPropertyDescriptor(target, prop) {
        const targetDescriptor = Reflect.getOwnPropertyDescriptor(target, prop);
        if (targetDescriptor) {
          return targetDescriptor;
        }
        if (typeof prop === "string") {
          return {
            configurable: true,
            enumerable: false,
            writable: false,
            value: (...args) => target.action(prop, ...args)
          };
        }
        return void 0;
      }
    });
  }
  /**
   * Sends an HTTP request to the manager actor.
   * @private
   * @template Request
   * @template Response
   * @param {string} method - The HTTP method.
   * @param {string} path - The path for the request.
   * @param {Request} [body] - The request body.
   * @returns {Promise<Response>} - A promise resolving to the response.
   * @see {@link https://rivet.gg/docs/manage#client}
   */
  async #sendManagerRequest(method, path, body) {
    try {
      const managerEndpoint = await this.#managerEndpointPromise;
      const res = await fetch(`${managerEndpoint}${path}`, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        body: body ? JSON.stringify(body) : void 0
      });
      if (!res.ok) {
        throw new ManagerError(`${res.statusText}: ${await res.text()}`);
      }
      return res.json();
    } catch (error) {
      throw new ManagerError(String(error), { cause: error });
    }
  }
  /**
   * Fetches the region information.
   * @private
   * @returns {Promise<Region | undefined>} - A promise resolving to the region or undefined.
   * @see {@link https://rivet.gg/docs/edge#Fetching-regions-via-API}
   */
  //async #fetchRegion(): Promise<Region | undefined> {
  //	try {
  //		// Fetch the connection info from the manager
  //		const { endpoint, project, environment } =
  //			await this.#sendManagerRequest<undefined, RivetConfigResponse>(
  //				"GET",
  //				"/rivet/config",
  //			);
  //
  //		// Fetch the region
  //		//
  //		// This is fetched from the client instead of the manager so Rivet
  //		// can automatically determine the recommended region using an
  //		// anycast request made from the client
  //		const url = new URL("/regions/resolve", endpoint);
  //		if (project) url.searchParams.set("project", project);
  //		if (environment) url.searchParams.set("environment", environment);
  //		const res = await fetch(url.toString());
  //
  //		if (!res.ok) {
  //			// Add safe fallback in case we can't fetch the region
  //			logger().error(
  //				"failed to fetch region, defaulting to manager region",
  //				{
  //					status: res.statusText,
  //					body: await res.text(),
  //				},
  //			);
  //			return undefined;
  //		}
  //
  //		const { region }: { region: Region } = await res.json();
  //
  //		return region;
  //	} catch (error) {
  //		// Add safe fallback in case we can't fetch the region
  //		logger().error(
  //			"failed to fetch region, defaulting to manager region",
  //			{
  //				error,
  //			},
  //		);
  //		return undefined;
  //	}
  //}
  /**
   * Disconnects from all actors.
   *
   * @returns {Promise<void>} A promise that resolves when the socket is gracefully closed.
   */
  async dispose() {
    if (this.#disposed) {
      logger().warn("client already disconnected");
      return;
    }
    this.#disposed = true;
    logger().debug("disposing client");
    const disposePromises = [];
    for (const handle of this[ACTOR_HANDLES_SYMBOL].values()) {
      disposePromises.push(handle.dispose());
    }
    await Promise.all(disposePromises);
  }
};
function createClient(managerEndpointPromise, opts) {
  const client = new ClientRaw2(managerEndpointPromise, opts);
  return new Proxy(client, {
    get: (target, prop, receiver) => {
      if (typeof prop === "symbol" || prop in target) {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value === "function") {
          return value.bind(target);
        }
        return value;
      }
      if (typeof prop === "string") {
        return {
          get: (opts2) => {
            return target.get(prop, opts2);
          },
          create: (opts2) => {
            return target.create(
              prop,
              opts2
            );
          },
          getWithId: (actorId, opts2) => {
            return target.getWithId(
              actorId,
              opts2
            );
          }
        };
      }
      return void 0;
    }
  });
}

export {
  ActorClientError,
  InternalError,
  ManagerError,
  ConnParamsTooLong,
  MalformedResponseMessage,
  NoSupportedTransport,
  ActionError,
  ActorHandleRaw,
  createClient
};
//# sourceMappingURL=chunk-HS7EZVSQ.js.map